/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import { withContext } from '@ba-ui-toolkit/context';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import { ListItem } from '@ba-ui-toolkit/list';
import ItemDnD from '@ba-ui-toolkit/itemdnd';
import FlexLayout, { FlexItem } from '@ba-ui-toolkit/flexlayout';
import Tile from '@ba-ui-toolkit/tile';
import Container from '@ba-ui-toolkit/container';
import TruncatedText from '@ba-ui-toolkit/truncatedtext';
import InputView from './InputView';
import styles from './DataView.scss';
import dataViewItemStyles from './dataViewItem.scss';

const dataViewClassName = styles.dataView.replace(/"/g, '');
const dataViewItemClassName = dataViewItemStyles.dataViewItem.replace(/"/g, '');
const { BaPropTypes, sanitize, getInheritedProps } = propsUtils;

function createIcon(icon, locationClass) {
	if (!icon) {
		return null;
	}
	const iconComponent =
		typeof icon === 'string' ? <SVGIcon focusable={false} iconId={icon} /> : icon;

	return locationClass ? (
		<div className={cx(`${dataViewClassName}__icon`, locationClass)}>{iconComponent}</div>
	) : (
		iconComponent
	);
}

export default class DataViewItem extends Component {
	static propTypes = {
		...ListItem.propTypes,
		disabled: PropTypes.bool,
		readOnly: PropTypes.bool,
		dndable: PropTypes.bool,
		traverseable: BaPropTypes.deprecated(
			'It will be removed in Sprint 39.1, use "traversable" instead'
		),
		traversable: PropTypes.bool,
		readable: PropTypes.bool,
		executable: PropTypes.bool,
		writeable: PropTypes.bool,
		className: PropTypes.string,
		style: PropTypes.object,
		onFileMove: PropTypes.func,
	};
	static defaultProps = {
		traversable: false,
		readOnly: false,
		disabled: false,
		readable: true,
		executable: true,
		writeable: true,
		className: '',
		style: {},
		onFileMove: () => {},
	};
	static Col = ({ icon, cols, content }) => (
		<Container className={cx(`${dataViewItemClassName}-container`, 'col')}>
			<FlexLayout>
				<FlexItem className={`${dataViewItemClassName}__icon `}>
					{createIcon(icon)}
				</FlexItem>
				{cols.map((col, index) => (
					<FlexItem key={index} grow={1} className={`${dataViewItemClassName}__item `}>
						<TruncatedText value={content[col]} />
						{/* {content[col]} */}
					</FlexItem>
				))}
			</FlexLayout>
		</Container>
	);

	static Col2 = ({ children, ...others }) => (
		<FlexLayout className={cx(`${dataViewItemClassName}-container`, 'col')} {...others}>
			{children}
		</FlexLayout>
	);

	static Cell = ({ children, grow, ...others }) => (
		<FlexItem
			grow={grow || 1}
			className={cx(`${dataViewItemClassName}-container`, 'cell')}
			{...others}
		>
			{children}
		</FlexItem>
	);

	static Tile = props => <Tile {...props} />;

	static Label = ({ icon, title, subTitle, subTitleSecondary }) => (
		<Container className={cx(`${dataViewItemClassName}-container`, 'label')}>
			<FlexLayout>
				<FlexItem className={cx(`${dataViewItemClassName}__icon`)}>
					{createIcon(icon)}
				</FlexItem>
				<FlexItem grow={1} className={`${dataViewItemClassName}__content `}>
					<div className={`${dataViewItemClassName}__title`}>{title}</div>
					<FlexLayout
						direction="row"
						justifyContent="space-between"
						className={`${dataViewItemClassName}__content `}
					>
						<div className={`${dataViewItemClassName}__subTitle`}>{subTitle}</div>
						<div className={`${dataViewItemClassName}__subTitle`}>
							{subTitleSecondary}
						</div>
					</FlexLayout>
				</FlexItem>
			</FlexLayout>
		</Container>
	);

	static Input = ({ icon, ...props }) => (
		<Container className={cx(`${dataViewItemClassName}-container`, 'label')}>
			<FlexLayout>
				<FlexItem className={cx(`${dataViewItemClassName}__icon`)}>
					{createIcon(icon)}
				</FlexItem>
				<InputView {...props} />
			</FlexLayout>
		</Container>
	);

	_getDropProps = ({ id }) => ({ id });
	_onBeginDrag = ({ id, type, defaultName }) => ({ id, type, defaultName });
	_canDrop = (target, source) => {
		if (target && source) {
			return target.id !== source.id;
		}

		return true;
	};
	_onDrop = (target, source) => {
		const { onFileMove } = this.props;
		if (target && source) {
			const targetId = target && target.id;
			const sourceId = source && source.id;
			onFileMove && onFileMove({ sourceId, targetId });
		}
	};
	render() {
		const {
			dndable,
			disabled,
			readOnly,
			readable,
			writeable,
			traverseable,
			traversable,
			executable,
			className,
			style,
			...others
		} = this.props;
		const listItemPassthroughProps = getInheritedProps(others, ListItem);

		const listItemClassName = cx(dataViewItemClassName, {
			disabled,
			readOnly,
			traverseable,
			traversable,
			readable,
			executable,
			writeable,
		});

		const listItemProps = {
			...sanitize(others, DataViewItem),
			...listItemPassthroughProps,
			className: `${listItemClassName} ${className}`,
			style,
		};

		if (dndable) {
			const DnDLisItem = ItemDnD(ListItem, {
				dropItemType: 'DATAVIEW',
				dragItemType: 'DATAVIEW',
				simpleHoverStates: false,
			});

			return (
				<DnDLisItem
					getDropProps={this._getDropProps}
					onBeginDrag={this._onBeginDrag}
					canDrop={this._canDrop}
					onDrop={this._onDrop}
					{...listItemProps}
				>
					{this.props.children}
				</DnDLisItem>
			);
		}

		return (
			<ListItem data-tid="ba-common-dataview-item" {...listItemProps}>
				{this.props.children}
			</ListItem>
		);
	}
}

const ContextDataViewItem = withContext(...Object.keys(DataViewItem.propTypes))(DataViewItem);

export { ContextDataViewItem };
